import React, { useEffect } from 'react';
import { bool, func, string } from 'prop-types';

import { connect } from 'react-redux';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import { get } from 'lodash';

import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';

import EsmsDialog from '../../components/EsmsDialog';

import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as DeviceActions from '../../actions/deviceActions';
import * as UiActions from '../../actions/uiActions';

import classes from './ManageNiagaraDeviceModal.module.css';

const ValidationSchema = Yup.object().shape({
    name: Yup.string()
        .max(40)
        .required(),
    profileName: Yup.string()
        .required(),
    type: Yup.string()
        .required(),
    obixPath: Yup.string()
        .required()
});

const ManageNiagaraDeviceModal = ({
    close,
    open,
    title,
    isLoading,
    toggleLoading,
    errors,
    profiles,
    clearErrors,
    ...props
}) => {

    useEffect(() => {
        clearErrors();
    },[]);

    const formik = useFormik({
        initialValues: {
            name:        get(props, 'device.name', ''),
            profileName: get(props, 'device.basProfileId', ''),
            type:        get(props, 'device.type', ''),
            obixPath:    get(props, 'device.oBIXPath', '')
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const {
                agent,
                companyInfo,
                manageDevice,
                device
            } = props;

            const configurationId = get(props, 'agent.driverConfiguration[0].id', '');
            const basType = get(props, 'agent.driverConfiguration[0].basType', '');

            const deviceData = {
                configurationId,
                name:         formik.values.name,
                type:         formik.values.type,
                oBIXPath:     formik.values.obixPath,
                basProfileId: formik.values.profileName,
                basAgentId:   agent.id
            };

            const connectionData = {
                agentId:   agent.id,
                basType,
                companyId: companyInfo.companyId,
                configurationId
            };

            let isUpdated;

            clearErrors();
            setSubmitting(true);

            try {
                if (!isLoading) {
                    toggleLoading(true);

                    isUpdated = await manageDevice(device.id, deviceData, connectionData);
                }

                if (isUpdated) handleClose();
            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    const handleClose = () => {
        toggleLoading(false);
        clearErrors();
        close();
    };

    return (
        <EsmsDialog
            errors={errors}
            open={open}
            onClose={handleClose}
            disabled={formik.isSubmitting}
            onRendered={formik.handleReset}
            title={title}
            onConfirm={formik.handleSubmit}
            confirmText="Update"
            isLoading={isLoading}
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelContent}>
                        <p>
                            Enter a unique Device Name.
                        </p>
                        <p>
                            Choose the Profile for the Niagara Device. You can select a Key2Act Profile or a Custom Profile.
                        </p>
                        <p>
                            Enter the specific device Type.
                        </p>
                        <p>
                            Verify the oBIX path associated with the Device.
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="name"
                                name="name"
                                type="text"
                                value={formik.values.name}
                                label="Device Name"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                error={Boolean(formik.errors.name) && Boolean(formik.touched.name)}
                                helperText={Boolean(formik.touched.name) && formik.errors.name}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <InputLabel
                                htmlFor="profileName"
                                classes={{
                                    focused: classes.cssFocused
                                }}
                                error={Boolean(formik.errors.profileName) && Boolean(formik.touched.profileName)}
                            >
                                Profile
                            </InputLabel>
                            <Select
                                value={formik.values.profileName}
                                onChange={formik.handleChange}
                                className={classes.select}
                                inputProps={{
                                    name:   'profileName',
                                    id:     'profileName',
                                    onBlur: formik.handleBlur
                                }}
                                error={Boolean(formik.errors.profileName) && Boolean(formik.touched.profileName)}
                            >
                                {
                                    profiles.map(profile =>
                                        profile.basType.toLowerCase() === 'niagara' &&
                                        <MenuItem
                                            className={classes.selectMenuItem}
                                            value={profile.id}
                                            key={profile.id}
                                        >
                                            {profile.name}
                                        </MenuItem>
                                    )
                                }
                            </Select>
                            {formik.touched.profileName &&
                                formik.errors.profileName && <FormHelperText className={classes.error}>{formik.errors.profileName}</FormHelperText>}
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="type"
                                name="type"
                                type="text"
                                value={formik.values.type}
                                label="Type"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                error={Boolean(formik.errors.type) && Boolean(formik.touched.type)}
                                helperText={Boolean(formik.touched.type) && formik.errors.type}
                            />
                        </FormControl>
                        <FormControl className={classes.inputWrapper}>
                            <TextField
                                id="obixPath"
                                name="obixPath"
                                type="text"
                                value={formik.values.obixPath}
                                label="oBIX Path"
                                onChange={formik.handleChange}
                                InputLabelProps={{
                                    classes: {
                                        error:   classes.error,
                                        focused: classes.labelFocused,
                                        root:    classes.label
                                    }
                                }}
                                InputProps={{
                                    onBlur:  formik.handleBlur,
                                    classes: {
                                        error:     classes.cssErrorUnderline,
                                        underline: classes.cssUnderline,
                                        root:      classes.input
                                    }
                                }}
                                error={Boolean(formik.errors.obixPath) && Boolean(formik.touched.obixPath)}
                                helperText={Boolean(formik.touched.obixPath) && formik.errors.obixPath}
                            />
                        </FormControl>
                    </div>
                </div>
            </div>
        </EsmsDialog>
    );

};

ManageNiagaraDeviceModal.propTypes = {
    close: func.isRequired,
    open:  bool.isRequired,
    title: string.isRequired
};

const mapStateToProps = state => {
    return {
        companyInfo: state.companyInfo,
        errors:      state.messages.errors
    };
};

const mapDispatchToProps = {
    ...ApplicationMessageActions,
    ...DeviceActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(ManageNiagaraDeviceModal);
